import React, { useState, useMemo } from 'react';
import { Presentation, DollarSign, Users, Ticket, BarChart3 } from 'lucide-react';
import { Card, Input } from '../ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

export function ConferencePnlSimulator() {
  const [fixedCosts, setFixedCosts] = useState<number>(45000); // Venue, AV, Keynote
  const [fbCostPerHead, setFbCostPerHead] = useState<number>(120);
  const [ticketPrice, setTicketPrice] = useState<number>(350);
  const [sponsorRevenue, setSponsorRevenue] = useState<number>(25000);
  const [expectedAttendees, setExpectedAttendees] = useState<number>(300);

  const model = useMemo(() => {
    // Break-even attendees = (Fixed Costs - Sponsor Revenue) / (Ticket Price - Variable Costs)
    const marginPerAttendee = ticketPrice - fbCostPerHead;
    const netFixedCosts = Math.max(0, fixedCosts - sponsorRevenue);
    
    const breakevenAttendees = marginPerAttendee > 0 ? Math.ceil(netFixedCosts / marginPerAttendee) : 0;
    
    // Current scenario
    const totalRevenue = (expectedAttendees * ticketPrice) + sponsorRevenue;
    const totalCosts = fixedCosts + (expectedAttendees * fbCostPerHead);
    const netProfit = totalRevenue - totalCosts;
    const isProfitable = netProfit >= 0;

    // Generate graph data for different attendance scenarios
    const data: { attendees: string; profit: number }[] = [];
    const steps = [100, 200, 300, 400, 500];
    
    steps.forEach(attendees => {
      const rev = (attendees * ticketPrice) + sponsorRevenue;
      const cost = fixedCosts + (attendees * fbCostPerHead);
      data.push({
        attendees: `${attendees} ppl`,
        profit: rev - cost
      });
    });

    return {
      marginPerAttendee,
      breakevenAttendees,
      totalRevenue,
      totalCosts,
      netProfit,
      isProfitable,
      data
    };
  }, [fixedCosts, fbCostPerHead, ticketPrice, sponsorRevenue, expectedAttendees]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-orange-600 to-red-800 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Presentation className="w-6 h-6 mr-3 text-orange-200" />
            Conference P&L Simulator
          </h2>
          <p className="text-orange-100 max-w-xl">
            Stress-test your annual conference budget. Calculate exactly how many tickets you need to sell to break even after factoring in venue costs, food & beverage, and sponsorships.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Controls */}
        <div className="md:col-span-1 space-y-6">
          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Total Fixed Costs ($)</label>
              <Input 
                type="number" 
                value={fixedCosts} 
                onChange={(e) => setFixedCosts(Number(e.target.value))}
                className="font-bold"
              />
              <p className="text-xs text-slate-400 mt-1">Venue rental, A/V, speakers, marketing.</p>
            </div>
            
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">F&B Cost Per Attendee ($)</label>
              <Input 
                type="number" 
                value={fbCostPerHead} 
                onChange={(e) => setFbCostPerHead(Number(e.target.value))}
              />
              <p className="text-xs text-slate-400 mt-1">Variable cost per head (catering, swag).</p>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Ticket className="w-4 h-4 mr-2" /> Ticket Price ($)
              </label>
              <Input 
                type="number" 
                value={ticketPrice} 
                onChange={(e) => setTicketPrice(Number(e.target.value))}
                className="font-bold border-orange-200 focus-visible:ring-orange-500"
              />
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Sponsorship Revenue ($)</label>
              <Input 
                type="number" 
                value={sponsorRevenue} 
                onChange={(e) => setSponsorRevenue(Number(e.target.value))}
              />
            </div>
          </Card>
        </div>

        {/* Results */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="p-6 border-orange-200 dark:border-orange-900 shadow-xl bg-orange-50 dark:bg-orange-900/10">
              <h3 className="text-sm font-bold text-orange-700 dark:text-orange-400 uppercase tracking-wider mb-2 flex items-center">
                <Users className="w-4 h-4 mr-2" /> Break-Even Attendance
              </h3>
              <div className="text-5xl font-black text-orange-600 dark:text-orange-400">
                {model.breakevenAttendees}
              </div>
              <p className="text-sm text-orange-800 dark:text-orange-300 mt-2">
                You must sell {model.breakevenAttendees} tickets to cover all fixed costs.
              </p>
            </Card>

            <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 text-white">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Expected Scenario</h3>
              <div className="flex items-center gap-3 mb-4">
                <Input 
                  type="number" 
                  value={expectedAttendees} 
                  onChange={(e) => setExpectedAttendees(Number(e.target.value))}
                  className="w-24 bg-slate-800 border-slate-700 text-white"
                />
                <span className="text-slate-300">Attendees</span>
              </div>
              
              <div className="pt-4 border-t border-slate-800">
                <div className="text-xs text-slate-400 uppercase tracking-wider font-bold mb-1">Projected Profit</div>
                <div className={`text-4xl font-black ${model.isProfitable ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(model.netProfit)}
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 h-[350px]">
            <h3 className="text-lg font-bold mb-6 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-orange-500" /> Profitability by Attendance Scenario
            </h3>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={model.data} margin={{ top: 5, right: 20, bottom: 25, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
                <XAxis 
                  dataKey="attendees" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickMargin={10}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tickFormatter={(val) => `$${val/1000}k`}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  formatter={(value: any) => formatCurrency(Number(value))}
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar 
                  dataKey="profit" 
                  name="Net Profit"
                  radius={[4, 4, 0, 0]}
                >
                  {model.data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? '#10b981' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

      </div>
    </div>
  );
}
